import React from 'react';
import {
    X,
    ArrowUp,
    ArrowDown,
    Minus,
    Building2,
    DollarSign,
    Percent,
    Layers,
    Users,
    AlertCircle,
    Check,
    Award
} from 'lucide-react';

interface ComparisonItem {
    id: string;
    name: string;
    type: 'sba' | 'development';
    data: any;
}

interface ScenarioComparisonProps {
    items: ComparisonItem[];
    onRemove: (id: string) => void;
    onClear: () => void;
}

const ScenarioComparison: React.FC<ScenarioComparisonProps> = ({ items, onRemove, onClear }) => {
    const formatNumber = (num: number) => new Intl.NumberFormat('en-IN').format(Math.round(num));
    const formatCurrency = (num: number) => {
        if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
        if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
        return `₹${formatNumber(num)}`;
    };

    // Get comparison indicator (up/down/same)
    const getComparisonIndicator = (value: number, values: number[], higherIsBetter: boolean) => {
        const max = Math.max(...values);
        const min = Math.min(...values);

        if (values.length < 2 || max === min) {
            return <Minus className="w-3 h-3 text-gray-400" />;
        }

        if (higherIsBetter) {
            if (value === max) return <ArrowUp className="w-3 h-3 text-emerald-500" />;
            if (value === min) return <ArrowDown className="w-3 h-3 text-red-500" />;
        } else {
            if (value === min) return <ArrowUp className="w-3 h-3 text-emerald-500" />;
            if (value === max) return <ArrowDown className="w-3 h-3 text-red-500" />;
        }
        return <Minus className="w-3 h-3 text-gray-400" />;
    };

    if (items.length === 0) {
        return (
            <div className="bg-gray-50 rounded-xl p-6 text-center text-gray-500">
                <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Add scenarios to compare</p>
                <p className="text-xs text-gray-400 mt-1">
                    Click "Add to Compare" on scenarios you want to analyze
                </p>
            </div>
        );
    }

    // Extract metrics for comparison
    const getMetrics = (item: ComparisonItem) => {
        if (item.type === 'sba') {
            return {
                towers: item.data.tower_count || 1,
                floors: item.data.tower_configuration?.floors_per_tower || 0,
                totalUnits: item.data.units?.total_units || 0,
                saleableArea: item.data.areas?.total_saleable_area_sqm || 0,
                efficiency: (item.data.areas?.efficiency_ratio || 0) * 100,
                parking: item.data.parking?.car_parking_spaces || 0,
                feasibility: item.data.feasibility || 'Unknown'
            };
        } else {
            // Development scenario
            return {
                buildableArea: item.data.cost_analysis?.buildable_area_sqft || 0,
                constructionCost: item.data.cost_analysis?.total_construction_cost || 0,
                revenue: item.data.revenue_potential?.gross_revenue || item.data.revenue_potential?.annual_rental_income || 0,
                margin: item.data.revenue_potential?.gross_margin || item.data.revenue_potential?.cap_rate || 0,
                floors: item.data.floor_plate?.total_floors || 0,
                requiresApproval: item.data.requires_approval || false
            };
        }
    };

    const isSBAComparison = items[0]?.type === 'sba';
    const metrics = items.map(getMetrics);

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                    <Award className="w-5 h-5" />
                    <h3 className="font-medium">Scenario Comparison</h3>
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                        {items.length} items
                    </span>
                </div>
                <button
                    onClick={onClear}
                    className="text-white/80 hover:text-white text-xs flex items-center gap-1 px-2 py-1 hover:bg-white/10 rounded transition-colors"
                >
                    <X className="w-3 h-3" />
                    Clear All
                </button>
            </div>

            {/* Comparison Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left font-medium text-gray-600 w-32">Metric</th>
                            {items.map((item) => (
                                <th key={item.id} className="px-4 py-3 text-center font-medium text-gray-900 min-w-[150px]">
                                    <div className="flex items-center justify-center gap-2">
                                        <span className="truncate max-w-[100px]">{item.name}</span>
                                        <button
                                            onClick={() => onRemove(item.id)}
                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isSBAComparison ? (
                            // SBA Comparison Rows
                            <>
                                <tr className="border-b border-gray-100">
                                    <td className="px-4 py-3 text-gray-600 flex items-center gap-1">
                                        <Building2 className="w-4 h-4" />
                                        Towers
                                    </td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center font-medium">
                                            {m.towers}
                                        </td>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-100 bg-gray-50/50">
                                    <td className="px-4 py-3 text-gray-600 flex items-center gap-1">
                                        <Layers className="w-4 h-4" />
                                        Floors
                                    </td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center font-medium">
                                            G+{m.floors - 1}
                                        </td>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-100">
                                    <td className="px-4 py-3 text-gray-600 flex items-center gap-1">
                                        <Users className="w-4 h-4" />
                                        Total Units
                                    </td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <span className="font-bold text-emerald-600">{m.totalUnits}</span>
                                                {getComparisonIndicator(m.totalUnits, metrics.map((x: any) => x.totalUnits), true)}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-100 bg-gray-50/50">
                                    <td className="px-4 py-3 text-gray-600">Saleable Area</td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <span className="font-medium">{formatNumber(m.saleableArea)} m²</span>
                                                {getComparisonIndicator(m.saleableArea, metrics.map((x: any) => x.saleableArea), true)}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-100">
                                    <td className="px-4 py-3 text-gray-600 flex items-center gap-1">
                                        <Percent className="w-4 h-4" />
                                        Efficiency
                                    </td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center">
                                            <span className={`font-medium ${m.efficiency >= 70 ? 'text-emerald-600' : m.efficiency >= 60 ? 'text-blue-600' : 'text-yellow-600'}`}>
                                                {m.efficiency.toFixed(1)}%
                                            </span>
                                        </td>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-100 bg-gray-50/50">
                                    <td className="px-4 py-3 text-gray-600">Parking Spaces</td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center font-medium">{m.parking}</td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className="px-4 py-3 text-gray-600">Feasibility</td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${m.feasibility === 'Optimal' ? 'bg-emerald-100 text-emerald-700' :
                                                    m.feasibility === 'Good' ? 'bg-blue-100 text-blue-700' :
                                                        m.feasibility === 'Tight' ? 'bg-yellow-100 text-yellow-700' :
                                                            'bg-red-100 text-red-700'
                                                }`}>
                                                {m.feasibility}
                                            </span>
                                        </td>
                                    ))}
                                </tr>
                            </>
                        ) : (
                            // Development Scenario Comparison Rows
                            <>
                                <tr className="border-b border-gray-100">
                                    <td className="px-4 py-3 text-gray-600 flex items-center gap-1">
                                        <Layers className="w-4 h-4" />
                                        Floors
                                    </td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center font-medium">
                                            G+{m.floors - 1}
                                        </td>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-100 bg-gray-50/50">
                                    <td className="px-4 py-3 text-gray-600">Buildable Area</td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center font-medium">
                                            {formatNumber(m.buildableArea)} sqft
                                        </td>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-100">
                                    <td className="px-4 py-3 text-gray-600 flex items-center gap-1">
                                        <DollarSign className="w-4 h-4" />
                                        Construction Cost
                                    </td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <span className="font-medium text-red-600">{formatCurrency(m.constructionCost)}</span>
                                                {getComparisonIndicator(m.constructionCost, metrics.map((x: any) => x.constructionCost), false)}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-100 bg-gray-50/50">
                                    <td className="px-4 py-3 text-gray-600">Revenue</td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <span className="font-bold text-emerald-600">{formatCurrency(m.revenue)}</span>
                                                {getComparisonIndicator(m.revenue, metrics.map((x: any) => x.revenue), true)}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                                <tr className="border-b border-gray-100">
                                    <td className="px-4 py-3 text-gray-600 flex items-center gap-1">
                                        <Percent className="w-4 h-4" />
                                        Margin/Cap Rate
                                    </td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <span className={`font-bold ${m.margin >= 25 ? 'text-emerald-600' : m.margin >= 15 ? 'text-blue-600' : 'text-yellow-600'}`}>
                                                    {m.margin.toFixed(1)}%
                                                </span>
                                                {getComparisonIndicator(m.margin, metrics.map((x: any) => x.margin), true)}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className="px-4 py-3 text-gray-600 flex items-center gap-1">
                                        <AlertCircle className="w-4 h-4" />
                                        Approval
                                    </td>
                                    {metrics.map((m: any, i) => (
                                        <td key={i} className="px-4 py-3 text-center">
                                            {m.requiresApproval ? (
                                                <span className="text-yellow-600 flex items-center justify-center gap-1">
                                                    <AlertCircle className="w-4 h-4" />
                                                    Required
                                                </span>
                                            ) : (
                                                <span className="text-emerald-600 flex items-center justify-center gap-1">
                                                    <Check className="w-4 h-4" />
                                                    Not needed
                                                </span>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            </>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Best Option Summary */}
            {items.length >= 2 && (
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <Award className="w-4 h-4 text-emerald-600" />
                        Analysis Summary
                    </h4>
                    <div className="text-sm text-gray-600">
                        {isSBAComparison ? (
                            <p>
                                <strong className="text-gray-900">{items[metrics.findIndex((m: any) => m.totalUnits === Math.max(...metrics.map((x: any) => x.totalUnits)))]?.name}</strong>
                                {' '}offers the most units ({Math.max(...metrics.map((m: any) => m.totalUnits))}), while{' '}
                                <strong className="text-gray-900">{items[metrics.findIndex((m: any) => m.efficiency === Math.max(...metrics.map((x: any) => x.efficiency)))]?.name}</strong>
                                {' '}has the best efficiency ({Math.max(...metrics.map((m: any) => m.efficiency)).toFixed(1)}%)
                            </p>
                        ) : (
                            <p>
                                <strong className="text-gray-900">{items[metrics.findIndex((m: any) => m.margin === Math.max(...metrics.map((x: any) => x.margin)))]?.name}</strong>
                                {' '}offers the best return ({Math.max(...metrics.map((m: any) => m.margin)).toFixed(1)}% margin), while{' '}
                                <strong className="text-gray-900">{items[metrics.findIndex((m: any) => m.constructionCost === Math.min(...metrics.map((x: any) => x.constructionCost)))]?.name}</strong>
                                {' '}has the lowest cost ({formatCurrency(Math.min(...metrics.map((m: any) => m.constructionCost)))})
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScenarioComparison;
